import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Badge } from 'src/@/components/ui/badge';
import Iconify from 'src/components/Iconify';
import actionListaDiagnosticosPorChat from 'src/actions/diagnosticos/actionListaDiagnosticosPorChat';
import actionSolicitarDiagnostico from 'src/actions/diagnosticos/actionSolicitarDiagnostico';
import actionSolicitarMidia from 'src/actions/diagnosticos/actionSolicitarMidia';
import actionListaMidiasPorChat from 'src/actions/diagnosticos/actionListaMidiasPorChat';
import actionEncerrarGravacao from 'src/actions/diagnosticos/actionEncerrarGravacao';
import { fDateTime } from 'src/utils/formatTime';

const getTriggerLabel = (t) => ({
    recording: { label: t('chat.info.diagnostics.trigger.recording', 'Gravação'), className: 'bg-blue-50 text-blue-700 border-blue-200' },
    report: { label: t('chat.info.diagnostics.trigger.report', 'Report'), className: 'bg-orange-50 text-orange-700 border-orange-200' },
    attendant: { label: t('chat.info.diagnostics.trigger.attendant', 'Atendente'), className: 'bg-violet-50 text-violet-700 border-violet-200' },
});

const PENDING_TIMEOUT_MS = 45_000;

// Pedidos de print/gravação ainda em curso: enquanto houver um, o painel faz poll.
const MIDIA_ATIVAS = new Set(['pending', 'capturing', 'captured']);

// Um pedido 'pending' fica esperando o visitante clicar em aceitar/recusar —
// diferente do diagnóstico (coleta automática em segundos), aqui é uma pessoa
// que precisa notar o chat e decidir. Sem um teto, um pedido ignorado fica
// "aguardando o visitante" para sempre e se acumula na lista de ativos junto
// com pedidos genuinamente recentes, voltando a misturar tudo (o problema que
// esta seção existe para evitar). 10 min sem resposta = trata como "sem
// resposta" e manda pro histórico, do mesmo jeito que isExpired faz com
// diagnóstico — o status no banco continua 'pending' (o visitante ainda PODE
// aceitar depois), só a leitura do atendente muda.
const MIDIA_STALE_TIMEOUT_MS = 10 * 60_000;
const isMidiaStale = (m) =>
    m.status === 'pending' && m.created_at && Date.now() - new Date(m.created_at).getTime() > MIDIA_STALE_TIMEOUT_MS;
const isMidiaAtiva = (m) => MIDIA_ATIVAS.has(m.status) && !isMidiaStale(m);

// Rótulo + cor (+ dot) de cada estado do pedido de mídia, na visão do atendente.
// O dot deixa o card escaneável pela cor sem precisar reler o texto toda vez —
// importante quando há vários pedidos parecidos na mesma lista.
const getMidiaStatus = (t, kind) => {
    const isVideo = kind === 'video';
    return {
        pending: {
            label: t('chat.info.media.status.pending', 'Aguardando o visitante'),
            className: 'bg-amber-50 text-amber-700 border-amber-200',
            dot: 'bg-amber-500',
        },
        capturing: {
            label: isVideo
                ? t('chat.info.media.status.recording', 'Aceitou — gravando')
                : t('chat.info.media.status.capturing', 'Aceitou — capturando'),
            className: 'bg-green-50 text-green-700 border-green-200',
            dot: 'bg-green-500',
        },
        captured: {
            label: t('chat.info.media.status.captured', 'Capturado — aguardando envio'),
            className: 'bg-blue-50 text-blue-700 border-blue-200',
            dot: 'bg-blue-500',
        },
        done: {
            label: t('chat.info.media.status.done', 'Enviado'),
            className: 'bg-green-50 text-green-700 border-green-200',
            dot: 'bg-green-500',
        },
        declined: {
            label: t('chat.info.media.status.declined', 'Recusado'),
            className: 'bg-red-50 text-red-600 border-red-200',
            dot: 'bg-red-500',
        },
        cancelled: {
            label: t('chat.info.media.status.cancelled', 'Desistiu'),
            className: 'bg-gray-50 text-gray-500 border-gray-200',
            dot: 'bg-gray-400',
        },
    };
};

const getDurationOptions = (t) => ([
    { value: 0, label: t('chat.info.diagnostics.duration.instant', 'Instantâneo (dados já capturados)') },
    { value: 30, label: t('chat.info.diagnostics.duration.sec30', 'Observar por 30 segundos') },
    { value: 60, label: t('chat.info.diagnostics.duration.min1', 'Observar por 1 minuto') },
    { value: 120, label: t('chat.info.diagnostics.duration.min2', 'Observar por 2 minutos') },
    { value: 300, label: t('chat.info.diagnostics.duration.min5', 'Observar por 5 minutos') },
]);

const DEFAULT_CONFIG = {
    durationSec: 0,
    withSpeedTest: true,
    includeConsole: true,
    includeNetwork: true,
    consoleErrorsOnly: false,
    networkFailuresOnly: false,
    includeHistory: true,
};

const getFilterOptions = (t) => ([
    { key: 'withSpeedTest', label: t('chat.info.diagnostics.filters.speedTest', 'Teste de internet (ping/download/upload)') },
    { key: 'includeConsole', label: t('chat.info.diagnostics.filters.console', 'Logs de console') },
    { key: 'includeNetwork', label: t('chat.info.diagnostics.filters.network', 'Requisições de rede') },
    { key: 'consoleErrorsOnly', label: t('chat.info.diagnostics.filters.consoleErrorsOnly', 'Console: somente erros e avisos') },
    { key: 'networkFailuresOnly', label: t('chat.info.diagnostics.filters.networkFailuresOnly', 'Rede: somente falhas (erro/4xx/5xx)') },
    { key: 'includeHistory', label: t('chat.info.diagnostics.filters.includeHistory', 'Incluir dados capturados anteriormente') },
]);

const LEVEL_COLOR = {
    error: 'text-red-600',
    warn: 'text-amber-600',
    info: 'text-blue-600',
    debug: 'text-gray-400',
    log: 'text-gray-600',
};

// Coletas com janela de observação demoram (duração + speed test): o timeout
// de "sem resposta" considera a duração escolhida.
const isExpired = (diag) =>
    diag.status === 'pending' &&
    diag.created_at &&
    Date.now() - new Date(diag.created_at).getTime() >
        ((diag.options?.durationSec || 0) * 1000 + PENDING_TIMEOUT_MS);

const summaryLine = (snapshot, t) => {
    if (!snapshot) return '';
    const parts = [];
    if (snapshot.speedTest) {
        parts.push(t('chat.info.diagnostics.summary.connection', 'ping {{ping}}ms · ↓{{download}} · ↑{{upload}} Mbps', {
            ping: snapshot.speedTest.pingMs,
            download: snapshot.speedTest.downloadMbps,
            upload: snapshot.speedTest.uploadMbps,
        }));
    }
    const errs = (snapshot.console || []).filter((c) => c.level === 'error').length;
    parts.push(t('chat.info.diagnostics.summary.errors', '{{count}} erro(s) · {{total}} requisições', {
        count: errs,
        total: (snapshot.network || []).length,
    }));
    return parts.join(' — ');
};

const exportJson = (diag) => {
    const blob = new Blob([JSON.stringify(diag, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `diagnostico-${new Date(diag.created_at || Date.now()).toISOString().slice(0, 19)}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
};

function DetailRow({ label, value }) {
    if (value === null || value === undefined || value === '') return null;
    return (
        <div className="flex justify-between gap-3 py-1 border-b border-gray-50 dark:border-gray-800 last:border-0">
            <span className="text-[11px] text-gray-400 flex-shrink-0">{label}</span>
            <span className="text-[11px] text-gray-700 dark:text-gray-300 text-right break-all">{String(value)}</span>
        </div>
    );
}

DetailRow.propTypes = { label: PropTypes.string, value: PropTypes.any };

function DiagnosticoDialog({ diag, onClose }) {
    const { t } = useTranslation();
    const snapshot = diag?.snapshot || {};
    const st = snapshot.speedTest;
    const device = snapshot.device || {};

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} />
            <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col animate-in fade-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center">
                            <Iconify icon="eva:activity-outline" width={18} className="text-violet-600" />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{t('chat.info.diagnostics.dialog.title', 'Diagnóstico do visitante')}</p>
                            <p className="text-[11px] text-gray-400">{diag.created_at ? fDateTime(diag.created_at) : ''}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => exportJson(diag)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#7C3AED] bg-violet-50 hover:bg-violet-100 rounded-lg transition-colors"
                        >
                            <Iconify icon="eva:download-outline" width={14} />
                            {t('chat.info.diagnostics.dialog.exportJson', 'Exportar JSON')}
                        </button>
                        <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400">
                            <Iconify icon="eva:close-outline" width={18} />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
                    {/* Conexão */}
                    <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{t('chat.info.diagnostics.dialog.connection', 'Conexão')}</p>
                        {st ? (
                            <div className="grid grid-cols-4 gap-2">
                                {[
                                    { label: t('chat.info.diagnostics.dialog.ping', 'Ping'), value: `${st.pingMs} ms` },
                                    { label: t('chat.info.diagnostics.dialog.jitter', 'Jitter'), value: `${st.jitterMs} ms` },
                                    { label: t('chat.info.diagnostics.dialog.download', 'Download'), value: `${st.downloadMbps} Mbps` },
                                    { label: t('chat.info.diagnostics.dialog.upload', 'Upload'), value: `${st.uploadMbps} Mbps` },
                                ].map((item) => (
                                    <div key={item.label} className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-3 text-center">
                                        <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{item.value}</p>
                                        <p className="text-[10px] text-gray-400 mt-0.5">{item.label}</p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-[11px] text-gray-400">{t('chat.info.diagnostics.dialog.noSpeedTest', 'Teste de internet não executado nesta coleta.')}</p>
                        )}
                        {device.connection && (
                            <p className="text-[11px] text-gray-400 mt-2">
                                {t('chat.info.diagnostics.dialog.networkInfo', 'Rede: {{type}} · downlink {{downlink}} Mbps · RTT {{rtt}} ms · {{status}}', {
                                    type: device.connection.effectiveType || '?',
                                    downlink: device.connection.downlink ?? '?',
                                    rtt: device.connection.rtt ?? '?',
                                    status: device.online ? t('chat.info.diagnostics.dialog.online', 'online') : t('chat.info.diagnostics.dialog.offline', 'offline'),
                                })}
                            </p>
                        )}
                    </div>

                    {/* Dispositivo */}
                    <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{t('chat.info.diagnostics.dialog.device', 'Dispositivo')}</p>
                        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-3">
                            <DetailRow label={t('chat.info.diagnostics.dialog.page', 'Página')} value={device.pageUrl} />
                            <DetailRow label={t('chat.info.diagnostics.dialog.browser', 'Navegador')} value={device.userAgent} />
                            <DetailRow
                                label={t('chat.info.diagnostics.dialog.screen', 'Tela')}
                                value={device.screen && t('chat.info.diagnostics.dialog.screenValue', '{{screen}} (viewport {{viewport}}, DPR {{dpr}})', { screen: device.screen, viewport: device.viewport, dpr: device.devicePixelRatio })}
                            />
                            <DetailRow label={t('chat.info.diagnostics.dialog.language', 'Idioma')} value={device.language} />
                            <DetailRow label={t('chat.info.diagnostics.dialog.timezone', 'Timezone')} value={device.timezone} />
                            <DetailRow
                                label={t('chat.info.diagnostics.dialog.memory', 'Memória')}
                                value={device.deviceMemory ? t('chat.info.diagnostics.dialog.memoryValue', '{{memory}} GB', { memory: device.deviceMemory }) : null}
                            />
                        </div>
                    </div>

                    {/* Console */}
                    <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                            {t('chat.info.diagnostics.dialog.console', 'Console ({{count}})', { count: (snapshot.console || []).length })}
                        </p>
                        {(snapshot.console || []).length === 0 ? (
                            <p className="text-[11px] text-gray-400">{t('chat.info.diagnostics.dialog.noConsoleLogs', 'Nenhum log capturado.')}</p>
                        ) : (
                            <div className="bg-gray-900 rounded-xl p-3 max-h-56 overflow-y-auto font-mono text-[11px] space-y-0.5">
                                {(snapshot.console || []).map((c, i) => (
                                    <div key={i} className="flex gap-2">
                                        <span className="text-gray-500 flex-shrink-0">
                                            {new Date(c.ts).toLocaleTimeString('pt-BR')}
                                        </span>
                                        <span className={`flex-shrink-0 uppercase ${LEVEL_COLOR[c.level] || 'text-gray-400'}`}>{c.level}</span>
                                        <span className="text-gray-200 break-all">{c.text}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Requisições */}
                    <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                            {t('chat.info.diagnostics.dialog.requests', 'Requisições ({{count}})', { count: (snapshot.network || []).length })}
                        </p>
                        {(snapshot.network || []).length === 0 ? (
                            <p className="text-[11px] text-gray-400">{t('chat.info.diagnostics.dialog.noRequests', 'Nenhuma requisição capturada.')}</p>
                        ) : (
                            <div className="border border-gray-100 dark:border-gray-800 rounded-xl max-h-56 overflow-y-auto divide-y divide-gray-50 dark:divide-gray-800">
                                {(snapshot.network || []).map((n, i) => {
                                    const failed = n.error || (n.status !== null && n.status >= 400);
                                    return (
                                        <div key={i} className="flex items-center gap-2 px-3 py-1.5 text-[11px]">
                                            <span className="font-mono font-semibold text-gray-500 w-12 flex-shrink-0">{n.method}</span>
                                            <span className="flex-1 min-w-0 truncate text-gray-700 dark:text-gray-300" title={n.url}>{n.url}</span>
                                            <span className={`font-mono flex-shrink-0 ${failed ? 'text-red-600 font-bold' : 'text-green-600'}`}>
                                                {n.status ?? 'ERR'}
                                            </span>
                                            <span className="text-gray-400 flex-shrink-0 w-14 text-right">{n.duration}ms</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

DiagnosticoDialog.propTypes = { diag: PropTypes.object, onClose: PropTypes.func };

/**
 * Cabeçalho de subseção (título + contador de itens ativos + botão de ação).
 * Separa o rótulo do botão que abre o formulário — antes os dois eram o mesmo
 * elemento (um botão-pílula largo), o que fazia a seção inteira parecer "só
 * mais um botão" em vez de um bloco com identidade própria.
 */
function SectionHeader({ icon, title, activeCount, actionLabel, isOpen, onToggleAction }) {
    return (
        <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
                <Iconify icon={icon} width={13} className="text-gray-400 flex-shrink-0" />
                <span className="text-[10.5px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider truncate">
                    {title}
                </span>
                {activeCount > 0 && (
                    <span className="flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full text-[9px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400 flex-shrink-0">
                        {activeCount}
                    </span>
                )}
            </div>
            <button
                onClick={onToggleAction}
                className={`flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-md transition-colors flex-shrink-0 ${isOpen ? 'text-white bg-[#7C3AED] hover:bg-[#6d28d9]' : 'text-[#7C3AED] bg-violet-50 hover:bg-violet-100'}`}
            >
                <Iconify icon={isOpen ? 'eva:close-outline' : 'eva:plus-outline'} width={12} />
                {actionLabel}
            </button>
        </div>
    );
}

SectionHeader.propTypes = {
    icon: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    activeCount: PropTypes.number,
    actionLabel: PropTypes.string.isRequired,
    isOpen: PropTypes.bool,
    onToggleAction: PropTypes.func.isRequired,
};

/** Link discreto para revelar o histórico (itens já resolvidos) de uma subseção. */
function HistoryToggle({ open, count, onToggle }) {
    if (!count) return null;
    return (
        <button
            onClick={onToggle}
            className="flex items-center gap-1 text-[10px] font-semibold text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 mt-1"
        >
            <Iconify icon={open ? 'mdi:chevron-up' : 'mdi:chevron-down'} width={12} />
            {open
                ? `Ocultar histórico`
                : `Ver histórico (${count})`}
        </button>
    );
}

HistoryToggle.propTypes = { open: PropTypes.bool, count: PropTypes.number, onToggle: PropTypes.func.isRequired };

/**
 * Card de um pedido de print/gravação. `active` destaca visualmente (fundo e
 * borda âmbar) os que ainda aguardam alguma ação — do visitante ou do
 * atendente; itens resolvidos (enviado/recusado/desistiu) ficam neutros para
 * não competir por atenção com o que ainda está em aberto.
 */
function MidiaCard({ m, t, active, onEncerrar, encerrando }) {
    // 'pending' há muito tempo sem resposta: mostra "Sem resposta" (neutro) em
    // vez de "Aguardando o visitante" (âmbar) — senão o card do histórico
    // continua parecendo que está esperando algo, o que é justamente por que
    // ele foi tirado da lista de ativos.
    const stale = isMidiaStale(m);
    const info = stale
        ? { label: t('chat.info.media.status.stale', 'Sem resposta'), className: 'bg-gray-50 text-gray-500 border-gray-200', dot: 'bg-gray-400' }
        : getMidiaStatus(t, m.kind)[m.status] || getMidiaStatus(t, m.kind).pending;
    const gravandoAgora = m.kind === 'video' && m.status === 'capturing';
    return (
        <div
            className={`p-2.5 rounded-lg border transition-colors ${
                active
                    ? 'bg-amber-50/50 border-amber-100 dark:bg-amber-500/5 dark:border-amber-500/20'
                    : 'bg-white dark:bg-gray-800/50 border-gray-100 dark:border-gray-700'
            }`}
        >
            <div className="flex items-start gap-2">
                <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
                    <Iconify
                        icon={m.kind === 'video' ? 'eva:video-outline' : 'eva:camera-outline'}
                        width={12}
                        className="text-[#7C3AED]"
                    />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-1.5">
                        <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-200">
                            {m.kind === 'video'
                                ? t('chat.info.media.kind.video', 'Gravação de tela')
                                : t('chat.info.media.kind.image', 'Print da tela')}
                        </span>
                        {m.created_at && (
                            <span className="text-[9px] text-gray-400 flex-shrink-0">{fDateTime(m.created_at)}</span>
                        )}
                    </div>
                    <Badge variant="outline" className={`text-[10px] font-medium mt-1 inline-flex items-center gap-1 ${info.className}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${info.dot}`} />
                        {info.label}
                    </Badge>
                    {m.message && (
                        <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 truncate">“{m.message}”</p>
                    )}
                </div>
            </div>

            {gravandoAgora && (
                <button
                    onClick={() => onEncerrar(m.request_id)}
                    disabled={encerrando}
                    className="mt-2 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-[11px] font-semibold text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors disabled:opacity-60"
                >
                    <Iconify icon={encerrando ? 'svg-spinners:ring-resize' : 'eva:stop-circle-outline'} width={13} />
                    {t('chat.info.media.stopRecording', 'Encerrar gravação')}
                </button>
            )}

            {m.status === 'captured' && (
                <p className="text-[10px] text-gray-400 mt-1.5 pl-8">
                    {t('chat.info.media.awaitingSend', 'O visitante está revisando antes de enviar.')}
                </p>
            )}
        </div>
    );
}

MidiaCard.propTypes = {
    m: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
    active: PropTypes.bool,
    onEncerrar: PropTypes.func.isRequired,
    encerrando: PropTypes.bool,
};

ChatInfoDiagnosticos.propTypes = {
    chat: PropTypes.object.isRequired,
    user: PropTypes.object,
    onCountUpdate: PropTypes.func,
};

export default function ChatInfoDiagnosticos({ chat, user, onCountUpdate }) {
    const { t } = useTranslation();
    const triggerLabel = getTriggerLabel(t);
    const durationOptions = getDurationOptions(t);
    const filterOptions = getFilterOptions(t);
    const [diagnosticos, setDiagnosticos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [solicitando, setSolicitando] = useState(false);
    const [selected, setSelected] = useState(null);
    const [showConfig, setShowConfig] = useState(false);
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    const [showDiagHistorico, setShowDiagHistorico] = useState(false);
    // Pedido de print/gravação de tela ao visitante
    const [showMidia, setShowMidia] = useState(false);
    const [showMidiaHistorico, setShowMidiaHistorico] = useState(false);
    const [midiaMsg, setMidiaMsg] = useState('');
    const [midiaEnviando, setMidiaEnviando] = useState(null); // 'image' | 'video'
    const [midiaFeedback, setMidiaFeedback] = useState(null); // { ok, text }
    const [midias, setMidias] = useState([]);
    const [encerrando, setEncerrando] = useState(false);
    const midiaPollRef = useRef(null);
    const pollRef = useRef(null);
    const chatIdRef = useRef(chat?._id);
    chatIdRef.current = chat?._id;

    const buscar = useCallback(async () => {
        const requestedId = chat?._id;
        if (!requestedId) return [];
        try {
            const response = await actionListaDiagnosticosPorChat(requestedId);
            if (chatIdRef.current !== requestedId) return [];
            const data = response.data.data || [];
            setDiagnosticos(data);
            return data;
        } catch (error) {
            console.log('Erro ao buscar diagnósticos do chat', error);
            return [];
        }
    }, [chat?._id]);

    useEffect(() => {
        let cancelled = false;
        setDiagnosticos([]);
        setSelected(null);
        setShowConfig(false);
        setLoading(true);
        buscar().then((data) => {
            if (cancelled) return;
            if (data) setDiagnosticos(data);
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [buscar]);

    // Enquanto houver pendente não-expirado, faz poll a cada 3s.
    useEffect(() => {
        const hasActivePending = diagnosticos.some((d) => d.status === 'pending' && !isExpired(d));
        if (hasActivePending && !pollRef.current) {
            pollRef.current = setInterval(() => buscar(), 3000);
        }
        if (!hasActivePending && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [diagnosticos, buscar]);

    // O badge do acordeão (no sidebar) precisa somar as duas subseções — antes
    // só contava diagnósticos, então o número no cabeçalho nunca batia com a
    // quantidade de cards visíveis (ex.: 5 pedidos de mídia + 3 diagnósticos,
    // badge mostrando "3"). Também trocamos para contar só o que está ATIVO
    // (aguardando alguém), que é o que de fato pede atenção do atendente —
    // itens já resolvidos não precisam "pesar" no contador.
    useEffect(() => {
        if (!onCountUpdate) return;
        const midiasAtivas = midias.filter(isMidiaAtiva).length;
        const diagnosticosAtivos = diagnosticos.filter((d) => d.status === 'pending' && !isExpired(d)).length;
        onCountUpdate(midiasAtivas + diagnosticosAtivos);
    }, [midias, diagnosticos, onCountUpdate]);

    const handleSolicitar = async () => {
        setSolicitando(true);
        try {
            // Instantâneo sem histórico seria uma coleta vazia — força o histórico.
            const options = { ...config, includeHistory: config.durationSec === 0 ? true : config.includeHistory };
            await actionSolicitarDiagnostico(chat._id, { userId: user?.id, options });
            setShowConfig(false);
            await buscar();
        } catch (error) {
            console.log('Erro ao solicitar diagnóstico', error);
        } finally {
            setSolicitando(false);
        }
    };

    const toggleConfig = (key) => setConfig((prev) => ({ ...prev, [key]: !prev[key] }));

    /* Pedidos de print/gravação: andamento + encerrar gravação --------------- */

    const buscarMidias = useCallback(async () => {
        const requestedId = chat?._id;
        if (!requestedId) return [];
        try {
            const response = await actionListaMidiasPorChat(requestedId);
            if (chatIdRef.current !== requestedId) return [];
            const data = response.data.data || [];
            setMidias(data);
            return data;
        } catch (error) {
            console.log('Erro ao buscar pedidos de mídia', error);
            return [];
        }
    }, [chat?._id]);

    useEffect(() => {
        setMidias([]);
        buscarMidias();
    }, [buscarMidias]);

    // Enquanto houver pedido em aberto, faz poll a cada 3s para o atendente
    // ver o aceite/recusa e o fim da gravação sem precisar recarregar.
    useEffect(() => {
        const emAberto = midias.some((m) => MIDIA_ATIVAS.has(m.status));
        if (emAberto && !midiaPollRef.current) {
            midiaPollRef.current = setInterval(() => buscarMidias(), 3000);
        }
        if (!emAberto && midiaPollRef.current) {
            clearInterval(midiaPollRef.current);
            midiaPollRef.current = null;
        }
        return () => {
            if (midiaPollRef.current) {
                clearInterval(midiaPollRef.current);
                midiaPollRef.current = null;
            }
        };
    }, [midias, buscarMidias]);

    const handleEncerrarGravacao = async (requestId) => {
        setEncerrando(true);
        try {
            await actionEncerrarGravacao(chat._id, { requestId });
            await buscarMidias();
        } catch (error) {
            console.log('Erro ao encerrar gravação', error);
        } finally {
            setEncerrando(false);
        }
    };

    /**
     * Pede um print ('image') ou gravação de tela ('video') ao visitante. O
     * widget mostra um convite no chat — a captura só acontece se ele aceitar
     * (privacidade + o navegador exige gesto do usuário para gravar a tela).
     */
    const handleSolicitarMidia = async (kind) => {
        setMidiaEnviando(kind);
        setMidiaFeedback(null);
        try {
            await actionSolicitarMidia(chat._id, { kind, message: midiaMsg.trim() || undefined });
            setMidiaMsg('');
            setMidiaFeedback({
                ok: true,
                text: t('chat.info.media.sent', 'Pedido enviado. Aguardando o visitante aceitar...'),
            });
            // Sem isto o card de status não aparece e o poll nunca começa —
            // o atendente ficaria sem saber se o visitante aceitou ou recusou.
            await buscarMidias();
        } catch (error) {
            console.log('Erro ao solicitar mídia', error);
            setMidiaFeedback({
                ok: false,
                text: t('chat.info.media.failed', 'Não foi possível enviar o pedido.'),
            });
        } finally {
            setMidiaEnviando(null);
        }
    };

    const statusBadge = (diag) => {
        if (diag.status === 'done') {
            return (
                <Badge variant="outline" className="text-[10px] font-medium inline-flex items-center gap-1 bg-green-50 text-green-700 border-green-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                    {t('chat.info.diagnostics.status.done', 'Concluído')}
                </Badge>
            );
        }
        if (isExpired(diag)) {
            return (
                <Badge variant="outline" className="text-[10px] font-medium inline-flex items-center gap-1 bg-gray-50 text-gray-500 border-gray-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                    {t('chat.info.diagnostics.status.expired', 'Sem resposta')}
                </Badge>
            );
        }
        const duration = diag.options?.durationSec || 0;
        return (
            <Badge variant="outline" className="text-[10px] font-medium inline-flex items-center gap-1 bg-amber-50 text-amber-700 border-amber-200">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                {duration > 0 ? t('chat.info.diagnostics.status.collecting', 'Coletando ({{duration}}s)...', { duration }) : t('chat.info.diagnostics.status.waiting', 'Aguardando visitante...')}
            </Badge>
        );
    };

    // Ativo = precisa de atenção agora (aguardando visitante/coletando); o
    // resto (concluído, sem resposta) vai para o histórico, colapsado por
    // padrão, para os cards recentes não ficarem soterrados.
    const midiasAtivas = midias.filter(isMidiaAtiva);
    const midiasHistorico = midias.filter((m) => !isMidiaAtiva(m));
    const diagnosticosAtivos = diagnosticos.filter((d) => d.status === 'pending' && !isExpired(d));
    const diagnosticosHistorico = diagnosticos.filter((d) => !(d.status === 'pending' && !isExpired(d)));

    const renderDiagCard = (diag) => {
        const trigger = triggerLabel[diag.trigger] || triggerLabel.attendant;
        return (
            <div
                key={diag._id}
                onClick={() => diag.status === 'done' && setSelected(diag)}
                className={`p-2.5 bg-white dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 rounded-lg transition-all ${diag.status === 'done' ? 'hover:border-violet-200 dark:hover:border-violet-600 hover:shadow-sm cursor-pointer' : 'opacity-80'}`}
            >
                <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge variant="outline" className={`text-[10px] font-medium ${trigger.className}`}>{trigger.label}</Badge>
                    {statusBadge(diag)}
                </div>
                {diag.status === 'done' && (
                    <p className="text-xs text-gray-700 dark:text-gray-300 mt-1 truncate">{summaryLine(diag.snapshot, t)}</p>
                )}
                {diag.created_at && (
                    <p className="text-[10px] text-gray-400 mt-1">{fDateTime(diag.created_at)}</p>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-4">
            {/* ===== Print / gravação de tela ===== */}
            <section className="space-y-2">
                <SectionHeader
                    icon="eva:monitor-outline"
                    title={t('chat.info.media.sectionTitle', 'Print & gravação')}
                    activeCount={midiasAtivas.length}
                    actionLabel={t('chat.info.media.request', 'Pedir')}
                    isOpen={showMidia}
                    onToggleAction={() => setShowMidia((prev) => !prev)}
                />

                {showMidia && (
                <div className="space-y-2.5 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 animate-in fade-in slide-in-from-top-1 duration-200">
                    <div>
                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                            {t('chat.info.media.messageLabel', 'Recado (opcional)')}
                        </p>
                        <input
                            type="text"
                            value={midiaMsg}
                            onChange={(e) => setMidiaMsg(e.target.value)}
                            maxLength={300}
                            placeholder={t('chat.info.media.messagePlaceholder', 'Ex.: preciso ver a tela de pagamento')}
                            className="w-full text-xs border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:border-[#7C3AED]"
                        />
                    </div>

                    <div className="flex gap-2">
                        <button
                            onClick={() => handleSolicitarMidia('image')}
                            disabled={!!midiaEnviando}
                            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 text-xs font-semibold text-white bg-[#7C3AED] hover:bg-[#6d28d9] rounded-lg transition-colors disabled:opacity-60"
                        >
                            <Iconify icon={midiaEnviando === 'image' ? 'svg-spinners:ring-resize' : 'eva:camera-outline'} width={14} />
                            {t('chat.info.media.askScreenshot', 'Print')}
                        </button>
                        <button
                            onClick={() => handleSolicitarMidia('video')}
                            disabled={!!midiaEnviando}
                            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 text-xs font-semibold text-[#7C3AED] bg-white dark:bg-gray-800 border border-[#7C3AED] hover:bg-violet-50 rounded-lg transition-colors disabled:opacity-60"
                        >
                            <Iconify icon={midiaEnviando === 'video' ? 'svg-spinners:ring-resize' : 'eva:video-outline'} width={14} />
                            {t('chat.info.media.askRecording', 'Gravação')}
                        </button>
                    </div>

                    <p className="text-[10px] text-gray-400 leading-relaxed">
                        {t('chat.info.media.consentHint', 'O visitante recebe um convite no chat e escolhe o que compartilhar. Nada é capturado sem ele aceitar.')}
                    </p>

                    {midiaFeedback && (
                        <p className={`text-[10px] font-medium ${midiaFeedback.ok ? 'text-green-600' : 'text-red-500'}`}>
                            {midiaFeedback.text}
                        </p>
                    )}
                </div>
            )}

            {midias.length === 0 && !showMidia && (
                <p className="text-[10px] text-gray-400 py-1">
                    {t('chat.info.media.empty', 'Nenhum pedido de print ou gravação ainda.')}
                </p>
            )}

            {/* Ativos primeiro (é o que precisa de atenção agora) */}
            {midiasAtivas.length > 0 && (
                <div className="space-y-1.5">
                    {midiasAtivas.map((m) => (
                        <MidiaCard key={m._id} m={m} t={t} active onEncerrar={handleEncerrarGravacao} encerrando={encerrando} />
                    ))}
                </div>
            )}

            <HistoryToggle
                open={showMidiaHistorico}
                count={midiasHistorico.length}
                onToggle={() => setShowMidiaHistorico((prev) => !prev)}
            />
            {showMidiaHistorico && (
                <div className="space-y-1.5 animate-in fade-in slide-in-from-top-1 duration-200">
                    {midiasHistorico.map((m) => (
                        <MidiaCard key={m._id} m={m} t={t} active={false} onEncerrar={handleEncerrarGravacao} encerrando={encerrando} />
                    ))}
                </div>
            )}
            </section>

            <div className="border-t border-gray-100 dark:border-gray-800" />

            {/* ===== Diagnóstico técnico ===== */}
            <section className="space-y-2">
                <SectionHeader
                    icon="eva:activity-outline"
                    title={t('chat.info.diagnostics.sectionTitle', 'Diagnóstico técnico')}
                    activeCount={diagnosticosAtivos.length}
                    actionLabel={t('chat.info.diagnostics.requestShort', 'Solicitar')}
                    isOpen={showConfig}
                    onToggleAction={() => setShowConfig((prev) => !prev)}
                />

                {showConfig && (
                <div className="space-y-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 animate-in fade-in slide-in-from-top-1 duration-200">
                    <div>
                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{t('chat.info.diagnostics.durationLabel', 'Duração da coleta')}</p>
                        <select
                            value={config.durationSec}
                            onChange={(e) => setConfig((prev) => ({ ...prev, durationSec: Number(e.target.value) }))}
                            className="w-full text-xs border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:border-[#7C3AED]"
                        >
                            {durationOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                        {config.durationSec > 0 && (
                            <p className="text-[10px] text-gray-400 mt-1">
                                {t('chat.info.diagnostics.durationHint', 'O widget observa a navegação do visitante por esse período antes de enviar os dados.')}
                            </p>
                        )}
                    </div>

                    <div>
                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{t('chat.info.diagnostics.whatToCollect', 'O que coletar')}</p>
                        <div className="space-y-1.5">
                            {filterOptions.map((opt) => {
                                const disabled = opt.key === 'includeHistory' && config.durationSec === 0;
                                const checked = disabled ? true : config[opt.key];
                                return (
                                    <label
                                        key={opt.key}
                                        className={`flex items-center gap-2 text-[11px] ${disabled ? 'text-gray-400' : 'text-gray-600 dark:text-gray-300 cursor-pointer'}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            disabled={disabled}
                                            onChange={() => toggleConfig(opt.key)}
                                            className="accent-[#7C3AED] w-3.5 h-3.5"
                                        />
                                        {opt.label}
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    <button
                        onClick={handleSolicitar}
                        disabled={solicitando}
                        className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-[#7C3AED] hover:bg-[#6d28d9] rounded-lg transition-colors disabled:opacity-60"
                    >
                        {solicitando ? (
                            <Iconify icon="svg-spinners:ring-resize" width={14} />
                        ) : (
                            <Iconify icon="eva:paper-plane-outline" width={14} />
                        )}
                        {t('chat.info.diagnostics.startCollection', 'Iniciar coleta')}
                    </button>
                </div>
            )}

            {loading && diagnosticos.length === 0 && (
                <div className="text-center py-4">
                    <p className="text-xs text-gray-400">{t('common.buttons.loading', 'Carregando...')}</p>
                </div>
            )}

            {!loading && diagnosticos.length === 0 && !showConfig && (
                <div className="text-center py-4">
                    <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-2">
                        <Iconify icon="eva:activity-outline" width={20} className="text-gray-400" />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('chat.info.diagnostics.empty', 'Nenhum diagnóstico coletado ainda')}</p>
                </div>
            )}

            {diagnosticosAtivos.length > 0 && (
                <div className="space-y-1.5">
                    {diagnosticosAtivos.map((diag) => renderDiagCard(diag))}
                </div>
            )}

            <HistoryToggle
                open={showDiagHistorico}
                count={diagnosticosHistorico.length}
                onToggle={() => setShowDiagHistorico((prev) => !prev)}
            />
            {showDiagHistorico && (
                <div className="space-y-1.5 animate-in fade-in slide-in-from-top-1 duration-200">
                    {diagnosticosHistorico.map((diag) => renderDiagCard(diag))}
                </div>
            )}
            </section>

            {selected && <DiagnosticoDialog diag={selected} onClose={() => setSelected(null)} />}
        </div>
    );
}
